<template>
    <admin-page :result="result" description="Documento config/builder, impacta a todas las apps al instante">
        <skeleton-loader v-if="loading" type="rows" :count="5" />

        <template v-else>
            <div class="config-section-title">Flags</div>
            <div class="config-list">
                <div v-for="flag in flags" :key="flag.key" class="config-row">
                    <div class="config-row-body">
                        <div class="config-name">{{ flag.name }}</div>
                        <div class="config-desc">{{ flag.description }}</div>
                    </div>
                    <kimsa-toggle :value="values[flag.key] === true" @change="onToggle(flag, $event)" />
                </div>
            </div>

            <div class="config-section-title mt-6">Versiones mínimas</div>
            <div class="config-hint">
                Número de build, no la versión visible. Esta web es el build <b>{{ versionAppCode }}</b>.
                Vacío no fuerza actualización.
            </div>
            <div class="version-groups">
                <div v-for="group in versionGroups" :key="group.name" class="version-group">
                    <div class="version-group-title">{{ group.name }}</div>
                    <div v-for="platform in group.platforms" :key="platform.key" class="version-row">
                        <v-icon size="18" color="txtMuted">{{ platform.icon }}</v-icon>
                        <div class="version-platform">{{ platform.name }}</div>
                        <button-tooltip tooltip="Restar 1">
                            <template #activator="{ on, attrs }">
                                <v-btn icon x-small v-on="on" v-bind="attrs" class="version-step"
                                    :disabled="!canStepVersion(platform.key, -1)"
                                    @click="stepVersion(platform.key, -1)">
                                    <v-icon size="16">mdi-minus</v-icon>
                                </v-btn>
                            </template>
                        </button-tooltip>
                        <kimsa-text-field class="version-input" :value="versions[platform.key]"
                            @change="versions[platform.key] = $event" :inputMode="'numeric'"
                            dense hide-details placeholder="-" />
                        <button-tooltip tooltip="Sumar 1">
                            <template #activator="{ on, attrs }">
                                <v-btn icon x-small v-on="on" v-bind="attrs" class="version-step"
                                    :disabled="!canStepVersion(platform.key, 1)"
                                    @click="stepVersion(platform.key, 1)">
                                    <v-icon size="16">mdi-plus</v-icon>
                                </v-btn>
                            </template>
                        </button-tooltip>
                    </div>
                </div>
            </div>

            <v-btn color="primary" class="black--text config-btn mt-5" depressed :loading="saving"
                :disabled="!versionChanges.length" @click="$refs.confirm.show()">
                Guardar versiones
            </v-btn>

            <div class="config-section-title mt-8">Resto del documento</div>
            <div class="config-list">
                <div v-for="field in otherFields" :key="field.key" class="config-row config-row--stacked">
                    <div class="config-row-body">
                        <div class="config-name">{{ field.key }}</div>
                        <div class="config-desc">{{ field.hint }}</div>
                    </div>

                    <kimsa-toggle v-if="field.type === 'boolean'" :value="field.value === true"
                        @change="onGenericToggle(field, $event)" />

                    <kimsa-text-field v-else-if="field.type === 'primitive'" class="config-input" :value="field.value"
                        @change="field.value = $event" dense hide-details />

                    <v-textarea v-else class="config-json" :value="field.value" @change="field.value = $event"
                        solo flat hide-details rows="6" spellcheck="false" />
                </div>
            </div>

            <v-btn color="primary" class="black--text config-btn mt-5" depressed :loading="saving"
                :disabled="!otherFields.length" @click="$refs.confirmOther.show()">
                Guardar resto del documento
            </v-btn>
        </template>

        <kimsa-confirm ref="confirm" show-cancel title="Guardar configuración" :content="confirmContent"
            :persistent="false" :close-on-confirm="false" :loading="saving" @confirm="saveVersions" />
        <kimsa-confirm ref="confirmFlag" show-cancel title="Cambiar flag" :content="flagConfirmContent"
            :persistent="false" :close-on-confirm="false" :loading="saving" @confirm="saveFlag"
            @cancel="pendingFlag = null" />
        <kimsa-confirm ref="confirmOther" alert-mode show-cancel title="Guardar documento"
            content="Vas a sobrescribir los campos editados de <b>config/builder</b>. Los cambios impactan a todas las apps al instante."
            :persistent="false" :close-on-confirm="false" :loading="saving" @confirm="saveOtherFields" />
    </admin-page>
</template>

<script>
import KimsaToggle from '@/components/common/inputs/KimsaToggle.vue'
import KimsaTextField from '@/components/common/inputs/KimsaTextField.vue'
import KimsaConfirm from '@/components/common/KimsaConfirm.vue'
import ButtonTooltip from '@/components/common/ButtonTooltip.vue'
import SkeletonLoader from '@/components/common/SkeletonLoader.vue'
import AdminPage from '@/components/admin/AdminPage.vue'
import { db } from '@/utils/Firebase'
import { adminRouteGuard } from '@/utils/Admin'

const FLAGS = [
    {
        key: 'emailConfirmationViaFunctions',
        name: 'Confirmación de email por SendGrid',
        description: 'Apagado usa el email nativo de Firebase Auth.',
    },
    {
        key: 'passwordResetViaFunctions',
        name: 'Reset de contraseña por SendGrid',
        description: 'Apagado usa el email nativo de Firebase Auth.',
    },
]

const LEGACY_FIELDS = ['tutorials', 'tutorialsCompany']

const VERSION_GROUPS = [
    {
        name: 'App de entrenadores',
        platforms: [
            { key: 'coaches', name: 'Android', icon: 'mdi-android' },
            { key: 'coaches_ios', name: 'iOS', icon: 'mdi-apple' },
        ],
    },
    {
        name: 'App de alumnos',
        platforms: [
            { key: 'students', name: 'Android', icon: 'mdi-android' },
            { key: 'students_ios', name: 'iOS', icon: 'mdi-apple' },
        ],
    },
]

const VERSION_KEYS = VERSION_GROUPS.flatMap(group => group.platforms.map(platform => platform.key))

export default {
    name: 'AdminConfig',
    components: { KimsaToggle, KimsaTextField, KimsaConfirm, ButtonTooltip, SkeletonLoader, AdminPage },
    beforeRouteEnter: adminRouteGuard,
    data() {
        return {
            flags: FLAGS,
            versionGroups: VERSION_GROUPS,
            values: {},
            versions: {},
            originalVersions: {},
            otherFields: [],
            loading: false,
            saving: false,
            pendingFlag: null,
            result: null,
        }
    },
    computed: {
        versionChanges(vm = this) {
            const changes = []
            vm.versionGroups.forEach(group => group.platforms.forEach(platform => {
                const before = `${vm.originalVersions[platform.key] ?? ''}`.trim()
                const after = `${vm.versions[platform.key] ?? ''}`.trim()
                if (before === after) return
                changes.push({ name: `${group.name} · ${platform.name}`, before: before || '—', after: after || '—' })
            }))
            return changes
        },
        confirmContent(vm = this) {
            const lines = vm.versionChanges.map(change => `${change.name}: <b>${change.before} → ${change.after}</b>`)
            return `Vas a cambiar las versiones mínimas.<br>${lines.join('<br>')}<br>Las apps con un build menor quedan bloqueadas hasta actualizar.`
        },
        flagConfirmContent(vm = this) {
            if (!vm.pendingFlag) return ''
            return `Vas a ${vm.pendingFlag.value ? 'activar' : 'desactivar'} <b>${vm.pendingFlag.flag.name}</b>.`
        },
    },
    mounted() {
        this.loadConfig()
    },
    methods: {
        canStepVersion(key, delta) {
            const value = `${this.versions[key] ?? ''}`.trim()
            if (!/^\d+$/.test(value)) return false
            return delta > 0 || Number(value) > 0
        },
        stepVersion(key, delta) {
            const vm = this
            if (!vm.canStepVersion(key, delta)) return
            const value = Number(`${vm.versions[key]}`.trim())
            vm.$set(vm.versions, key, `${Math.max(0, value + delta)}`)
        },
        async loadConfig() {
            const vm = this
            await vm.withLoading(async () => {
                const doc = await db.collection('config').doc('builder').get()
                const data = doc.data() || {}
                vm.values = { ...data }
                const emptyVersions = Object.fromEntries(VERSION_KEYS.map(key => [key, '']))
                vm.versions = { ...emptyVersions, ...(data.version || {}) }
                vm.originalVersions = { ...vm.versions }
                vm.otherFields = vm.buildOtherFields(data)
            })
        },
        buildOtherFields(data) {
            const vm = this
            const handled = [...vm.flags.map(flag => flag.key), 'version', ...LEGACY_FIELDS]

            return Object.keys(data)
                .filter(key => !handled.includes(key))
                .sort()
                .map(key => {
                    const value = data[key]
                    if (typeof value === 'boolean') {
                        return { key, type: 'boolean', value, hint: 'true / false' }
                    }
                    if (value === null || typeof value !== 'object') {
                        return { key, type: 'primitive', value, hint: typeof value }
                    }
                    const count = Array.isArray(value) ? `${value.length} elementos` : `${Object.keys(value).length} claves`
                    return { key, type: 'json', value: JSON.stringify(value, null, 2), hint: `JSON · ${count}` }
                })
        },
        onGenericToggle(field, value) {
            field.value = value
            this.$refs.confirmOther.show()
        },
        async saveOtherFields() {
            const vm = this
            vm.saving = true
            try {
                const payload = {}
                for (const field of vm.otherFields) {
                    if (field.type === 'json') {
                        try {
                            payload[field.key] = JSON.parse(field.value)
                        } catch (e) {
                            vm.result = { type: 'error', message: `El campo ${field.key} no es JSON válido.` }
                            return
                        }
                    } else if (field.type === 'primitive' && typeof field.value === 'number') {
                        payload[field.key] = Number(field.value)
                    } else {
                        payload[field.key] = field.value
                    }
                }

                await db.collection('config').doc('builder').set(payload, { merge: true })
                vm.result = { type: 'success', message: 'Documento actualizado.' }
                await vm.loadConfig()
            } catch (err) {
                console.error('[adminConfig] otherFields', err)
                vm.result = { type: 'error', message: err.message || 'Error al guardar el documento.' }
            } finally {
                vm.saving = false
                vm.$refs.confirmOther?.hide()
            }
        },
        onToggle(flag, value) {
            this.pendingFlag = { flag, value }
            this.$refs.confirmFlag.show()
        },
        async saveFlag() {
            const vm = this
            vm.saving = true
            try {
                const { flag, value } = vm.pendingFlag
                await db.collection('config').doc('builder').update({ [flag.key]: value })
                vm.$set(vm.values, flag.key, value)
                vm.result = { type: 'success', message: `${flag.name}: ${value ? 'activado' : 'desactivado'}.` }
                await vm.loadConfig()
            } catch (err) {
                console.error('[adminConfig] flag', err)
                vm.result = { type: 'error', message: err.message || 'Error al guardar el flag.' }
            } finally {
                vm.saving = false
                vm.pendingFlag = null
                vm.$refs.confirmFlag?.hide()
            }
        },
        async saveVersions() {
            const vm = this
            vm.saving = true
            try {
                const version = Object.entries(vm.versions).reduce((acc, [key, value]) => {
                    const clean = `${value ?? ''}`.trim()
                    if (clean) acc[key] = /^\d+$/.test(clean) ? Number(clean) : clean
                    return acc
                }, {})
                await db.collection('config').doc('builder').update({ version })
                vm.result = { type: 'success', message: 'Versiones mínimas actualizadas.' }
                await vm.loadConfig()
            } catch (err) {
                console.error('[adminConfig] versions', err)
                vm.result = { type: 'error', message: err.message || 'Error al guardar las versiones.' }
            } finally {
                vm.saving = false
                vm.$refs.confirm?.hide()
            }
        },
    },
}
</script>

<style scoped>
.config-section-title {
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    color: var(--v-txtMuted-base);
    margin-bottom: 8px;
}

.config-hint {
    font-size: 11px;
    color: var(--v-txtMuted-base);
    margin: -4px 0 12px;
}

.version-groups {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
}

.version-group {
    flex: 1 1 240px;
    min-width: 0;
    border: 1px solid var(--v-outline-base);
    border-radius: 8px;
    padding: 10px 14px;
}

.version-group-title {
    font-size: 13px;
    font-weight: 600;
    color: var(--v-txtPrimary-base);
    margin-bottom: 4px;
}

.version-row {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 6px 0;
}

.version-platform {
    flex: 1;
    font-size: 12px;
    color: var(--v-txtMuted-base);
}

.version-input {
    width: 72px;
    flex-shrink: 0;
}

.version-input ::v-deep input {
    text-align: center;
}

.version-step {
    flex-shrink: 0;
}

.config-list {
    display: flex;
    flex-direction: column;
}

.config-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding: 12px 0;
    border-bottom: 1px solid var(--v-outline-base);
}

.config-row-body {
    flex: 1;
    min-width: 0;
}

.config-name {
    font-size: 13px;
    font-weight: 600;
    color: var(--v-txtPrimary-base);
}

.config-desc {
    font-size: 11px;
    color: var(--v-txtMuted-base);
    margin-top: 2px;
}

.config-input {
    width: 160px;
    flex-shrink: 0;
}

.config-btn {
    height: 40px !important;
    min-width: 180px;
    border-radius: 8px;
}

.config-row--stacked {
    flex-direction: column;
    align-items: stretch;
    gap: 8px;
}

.config-json ::v-deep textarea {
    font-family: monospace;
    font-size: 11px;
    line-height: 1.5;
}

@media (max-width: 600px) {
    .config-row {
        flex-direction: column;
        align-items: stretch;
        gap: 8px;
    }

    .config-input,
    .config-btn {
        width: 100%;
    }
}
</style>
